import { createAction, createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
// ! causes circular dependency - fix this TODO: @balraj
// import { RootState } from '../types/store';
// store
import { RootState } from '../../../store';
// store - actions
import {
  addAssignmentField,
  removeAssignmentField,
  resetLayout,
  toggleCompanyLogoVisibility,
  toggleTilesAlignment,
  replaceAssignmentField,
  toggleTileHeaderData,
  updateAssignmentField,
  updateLayoutColsPerRow,
  updateDescription,
  updateAssignments,
  updateLayout,
  updateTileSlotLayoutOrder,
  updateSortingState,
  updatePresentationSlideTitle,
  updatePrivateAndConfidential,
  updateCompanyOfficialLogo,
  updatePresentationSlideNumber,
  updateLayoutRowsPerSlide,
} from '../reducers/presentationSlice';
// api
import { matchesApi } from '../../../services/matches';
// types
import { ICredentialMetaData } from '../types/credential';
// util
import { AUTO_SAVE_DELAY } from '../util/constants';
import { ImgUploadDetails, updateMetadata } from '../util/common';
import { updateCredMetadata } from '../reducers/credentialsSlice';
import { ACLS } from '../../../constants/acls';

const autoSaveMiddleware = createListenerMiddleware<RootState>();

export const cancelPendingPresentationAutoSave = createAction(
  'credentialApp/cancelPendingPresentationAutoSave'
);

export const isPresentationAutoSaveAction = isAnyOf(
  updateDescription,
  updateLayoutColsPerRow,
  updateLayoutRowsPerSlide,
  resetLayout,
  toggleCompanyLogoVisibility,
  toggleTilesAlignment,
  replaceAssignmentField,
  toggleTileHeaderData,
  addAssignmentField,
  removeAssignmentField,
  updateAssignmentField,
  updateAssignments,
  updateLayout,
  updateTileSlotLayoutOrder,
  updateSortingState,
  updatePresentationSlideTitle,
  updatePrivateAndConfidential,
  updateCompanyOfficialLogo,
  updatePresentationSlideNumber,
  matchesApi.endpoints.uploadImg.matchFulfilled
);

// The cancellation barrier participates in listener concurrency so it can
// abort pending work without starting another save. For example, typing can
// schedule autosave A; opening the modal and explicitly saving dispatches this
// barrier, which cancels A instead of replacing it with autosave B.
const isPresentationAutoSaveListenerAction = isAnyOf(
  isPresentationAutoSaveAction,
  cancelPendingPresentationAutoSave
);

autoSaveMiddleware.startListening({
  matcher: isPresentationAutoSaveListenerAction,
  effect: async (action, listenerApi) => {
    try {
      // Cancel any in-progress instances of this listener
      listenerApi.cancelActiveListeners();

      if (cancelPendingPresentationAutoSave.match(action)) {
        return;
      }

      // Bind this save attempt to the credential that triggered it. For
      // example, an edit made on credential A must not save credential B if the
      // user navigates from A to B during the debounce.
      const initiatingCredRef = listenerApi.getState().presentation.credRef;
      let imgUploadDetails: ImgUploadDetails | undefined;
      // when uploading company logo there shouldn't be any auto save delay
      if (matchesApi.endpoints.uploadImg.matchFulfilled(action)) {
        // UI sends additional param in the api do decide from which part of UI the upload happens
        // this is used to update the correct metadata field e.g. assignments_override, company_logo
        const { source, uniqueKey, isAutoSave } = action.meta.arg.originalArgs;
        const { key, message } = action.payload;

        if (!isAutoSave) { // dont auto save uploaded img if not intended
          return;
        }

        imgUploadDetails = {
          key,
          source,
          message,
          uniqueKey,
          isAutoSave
        }
      } else {
        // Debounce auto-save to avoid saving too frequently
        await listenerApi.delay(AUTO_SAVE_DELAY);
      }

      // Read fresh state after the debounce so later presentation edits are
      // included. For example, changing rows and then tiles-per-row within one
      // debounce window saves both latest values, provided the credential is
      // still the one that initiated the save.
      const state = listenerApi.getState();
      const presentation = state.presentation;
      if (presentation.credRef !== initiatingCredRef) {
        return;
      }

      const credRef = initiatingCredRef;
      const credential = state.credentials.credentials.byId[credRef];
      if (!credential) {
        return;
      }

      const currentUserResult = matchesApi.endpoints.getCurrentUser.select()(state);
      const currentUserId = currentUserResult.data?.uid;
      const isCredAdmin = currentUserResult.data?.roles.includes(ACLS.FM_CRED_ADMIN_ACL);
      if (currentUserId !== credential.uid && !isCredAdmin) {
        // only auto save if the current user owns the credential or has Cred Admin access
        return;
      }

      const metadata: ICredentialMetaData = updateMetadata({
        currentMetadata: credential.metadata,
        presentationState: presentation,
        imgUploadDetails,
      });

      const updateRequest = listenerApi.dispatch(matchesApi.endpoints.updateCred.initiate({
        joid: credential.uid,
        credential: {
          ...credential,
          metadata,
        }
      }));

      // cancelActiveListeners() aborts the listener signal; bridge that signal
      // to RTK Query as well. For example, if request A is already in flight
      // when edit B arrives, A.abort() prevents its stale response from winning.
      const abortUpdateRequest = () => updateRequest.abort();
      listenerApi.signal.addEventListener('abort', abortUpdateRequest, { once: true });

      try {
        await updateRequest;
      } finally {
        listenerApi.signal.removeEventListener('abort', abortUpdateRequest);
      }

      if (listenerApi.signal.aborted) {
        return;
      }

      listenerApi.dispatch(updateCredMetadata({
        ref: credRef,
        metadata
      }))

    } catch (error) {
      if (listenerApi.signal.aborted) {
        return;
      }
      console.error("Error in effect of autoSaveMiddleware: ", error);
    }
  },
});


export default autoSaveMiddleware;
